import { Clock, Compass, Check, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface QuickModeEntryProps {
  onQuickMode: () => void;
  onFullMode: () => void;
}

export const QuickModeEntry = ({ onQuickMode, onFullMode }: QuickModeEntryProps) => {
  const [hoveredMode, setHoveredMode] = useState<'quick' | 'full' | null>(null);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
          Wie viel Zeit hast du?
        </h2>
        <p className="text-muted-foreground text-base">
          Wähle deinen bevorzugten Weg zur Entscheidungsanalyse
        </p>
      </div>

      <div className="grid gap-4">
        {/* Quick Mode Card */}
        <button
          onClick={onQuickMode}
          onMouseEnter={() => setHoveredMode('quick')}
          onMouseLeave={() => setHoveredMode(null)}
          className={cn(
            "group relative flex items-start gap-4 rounded-xl border p-6 transition-all duration-300 text-left overflow-hidden",
            "bg-card/50 backdrop-blur-sm",
            "hover:shadow-md hover:-translate-y-0.5 hover:border-primary/40 hover:bg-card"
          )}
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
            <Zap className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-lg text-foreground">Quick Decision Mode</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                <Clock className="h-3 w-3" />
                30 Sek.
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Beschreibe kurz deine Situation – ich frage nach, was ich brauche
            </p>
            <div className="flex items-center gap-4 pt-1 text-xs text-muted-foreground/70">
              <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Schnell</span>
              <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Fokussiert</span>
            </div>
          </div>
        </button>

        {/* Full Mode Card */}
        <button
          onClick={onFullMode}
          onMouseEnter={() => setHoveredMode('full')}
          onMouseLeave={() => setHoveredMode(null)}
          className={cn(
            "group relative flex items-start gap-4 rounded-xl border p-6 transition-all duration-300 text-left overflow-hidden",
            "bg-primary text-primary-foreground",
            "hover:shadow-lg hover:-translate-y-0.5 hover:shadow-primary/20"
          )}
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-foreground/10 text-primary-foreground transition-all duration-300">
            <Compass className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-lg">Geführter Modus</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-foreground/10 text-primary-foreground/90 text-xs font-medium">
                Empfohlen
              </span>
            </div>
            <p className="text-sm opacity-90 leading-relaxed">
              Schritt für Schritt durch den Prozess – für präzisere Analyse & bessere Ergebnisse
            </p>
            <div className="flex items-center gap-4 pt-1 text-xs opacity-70">
              <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Gründlich</span>
              <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Strukturiert</span>
              <span className="flex items-center gap-1"><Check className="h-3 w-3" /> Präzise</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};
